import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import { keyframes } from "@mui/system";

/**
 * CONVECTIVE LEGEND — solo tres casillas (sin título, sin bordes, sin card)
 * - Enfocada a peligro por tormentas (convectivo)
 * - Por defecto usa "pattern" (tramas) en lugar de iconos → estándar cartográfico
 * - Opcionalmente puedes activar símbolos alternativos: "wmo" (TS-/TS/TS+), "boltCount" (⚡xN)
 *
 * API:
 *   <TurbulenceLegend
 *     levels={LEVELS}
 *     symbolVariant="pattern"   // "pattern" | "wmo" | "boltCount" | "none"
 *     interactive={false}
 *     size="mini"               // "mini" | "md"
 *   />
 */

// Animación sutil para SEV
const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 1;    box-shadow: 0 0 0 rgba(255,255,255,0); }
  50%  { transform: scale(1.05); opacity: .95; box-shadow: 0 0 18px rgba(255,255,255,0.22); }
  100% { transform: scale(1);    opacity: 1;    box-shadow: 0 0 0 rgba(255,255,255,0); }
`;

// Paleta y códigos estándar aeronáuticos
const LEVELS = [
  { label: "Light",     code: "LGT", start: "#ffff00", end: "#ffff00" }, // Amarillo
  { label: "Moderate",  code: "MOD", start: "#ff8c00", end: "#ff8c00" }, // Ámbar
  { label: "Severe",    code: "SEV", start: "#e5322d", end: "#e5322d" }, // Rojo
];

const containerSx = (hideBelowWidth = 0) => ({
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none", // no capta eventos si no es interactivo
  mt: -1,
  opacity: 1,
  transition: "opacity .25s ease",
  [`@media (max-width:${hideBelowWidth}px)`]: { opacity: 0 },
});

const makeBg = (start, end) => end ? `linear-gradient(135deg, ${start} 8%, ${end} 92%)` : start;

// ---- Overlays (símbolos sin usar iconos grandes) ----
function overlayFor(levelCode, variant) {
  const code = `${levelCode}`.toUpperCase();
  if (variant === "none") return undefined;

  if (variant === "pattern") {
    // Tramas cartográficas inspiradas en leyendas convectivas (hatching/dots)
    if (code.includes("LGT")) {
      return {
        background: `radial-gradient(rgba(255,255,255,.32) 1px, transparent 1px)`,
        backgroundSize: "6px 6px",
        opacity: 0.6,
        mixBlendMode: "overlay",
      };
    }
    if (code.includes("MOD")) {
      return {
        background: `repeating-linear-gradient(45deg, rgba(255,255,255,.32) 0 6px, rgba(255,255,255,.08) 6px 12px)`,
        opacity: 0.7,
        mixBlendMode: "overlay",
      };
    }
    // SEV → cross-hatch
    return {
      background: `repeating-linear-gradient(45deg, rgba(255,255,255,.35) 0 6px, rgba(255,255,255,.08) 6px 12px), repeating-linear-gradient(-45deg, rgba(255,255,255,.25) 0 6px, rgba(255,255,255,.06) 6px 12px)`,
      opacity: 0.8,
      mixBlendMode: "overlay",
    };
  }

  if (variant === "wmo") {
    // Etiquetas tipo WMO/TAF/ SIGMET: TS-, TS, TS+
    if (code.includes("LGT")) return { content: '"TS-"', color: "#0B1220" };
    if (code.includes("MOD")) return { content: '"TS"', color: "#0B1220" };
    return { content: '"TS+"', color: "#0B1220" };
  }

  if (variant === "boltCount") {
    // Pequeños marcadores de rayo; 1/2/3 según nivel
    if (code.includes("LGT")) return { content: '"⚡"' };
    if (code.includes("MOD")) return { content: '"⚡⚡"' };
    return { content: '"⚡⚡⚡"' };
  }
}

/* Texto blanco/negro automático por contraste (YIQ) */
function textColorFor(hex) {
  if (!hex || hex.length < 6) return "#0B1220";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#0B1220" : "#FFFFFF";
}

export default function TurbulenceLegend({
  levels = LEVELS,
  hideBelowWidth = 0,
  interactive = false,
  onSelect,
  size = "mini", // "mini" | "md"
  symbolVariant = "pattern", // "pattern" | "wmo" | "boltCount" | "none"
  equalWidth = true,
}) {
  const isMini = size === "mini";

  return (
    <Box>
      <Box component="span" sx={{
        display:'block',
        width:'100%',           // asegura ancho completo
        textAlign:'center',     // centra el texto
        mb:1.2,
        typography:'caption',
        fontWeight:800,
        letterSpacing:'.025em',
        textTransform:'uppercase',
        color:'#23339bff',
        opacity:.65,
        lineHeight:1.05,
      }}>
          <ThunderstormIcon sx={{ fontSize: { xs: 11, sm: 11, md: 12 }, color: 'inherit', marginRight: 0.55 }} aria-hidden />
        Convective Risk
      </Box>
    <Box sx={containerSx(hideBelowWidth)}>
      {/* Solo las tres píldoras */}
      <Box sx={{ display: "inline-flex", borderRadius: 999, overflow: "hidden", pointerEvents: interactive ? "auto" : "none" }}>
        {levels.map((lvl, i) => {
          const code = (lvl.code ?? lvl.label).toString().toUpperCase();
          const after = overlayFor(code, symbolVariant);

          return (
            <Tooltip key={lvl.code ?? lvl.label} title={`${lvl.label} (${lvl.code})`} arrow placement="top" disableInteractive>
              <Button
                disableElevation
                disableRipple
                aria-label={`${lvl.label}`}
                onClick={interactive && onSelect ? () => onSelect(lvl.code ?? lvl.label) : undefined}
                sx={{
                  position: "relative",
                  minWidth: equalWidth ? (isMini ? 70 : 82) : 0,
                  px: isMini ? 1.0 : 1.2,
                  py: isMini ? 0.44 : 0.58,
                  fontSize: isMini ? ".62rem" : ".7rem",
                  fontWeight: 600,
                  letterSpacing: ".02em",
                  lineHeight: 1,
                  textTransform: "none",
                  color: textColorFor(lvl.start),
                  backgroundColor: `rgba(${parseInt(lvl.start.slice(1, 3), 16)}, ${parseInt(lvl.start.slice(3, 5), 16)}, ${parseInt(lvl.start.slice(5, 7), 16)}, 0.8)`,
                  backdropFilter: "blur(20px) saturate(190%) contrast(1.1)",
                  borderRadius: 0,
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  borderTop: "2.5px solid rgba(255, 255, 255, 0.5)",
                  borderRight: i < levels.length - 1 ? 0 : "1px solid rgba(255, 255, 255, 0.25)",
                  boxShadow: `
                    0 12px 40px 0 rgba(0, 0, 0, 0.25),
                    inset 0 1px 1px 0 rgba(255, 255, 255, 0.3),
                    inset 0 -1px 0px 0 rgba(0, 0, 0, 0.1)
                  `,
                  transition: "transform .2s cubic-bezier(0.4, 0, 0.2, 1), background-color .25s ease, box-shadow .2s ease",
                  "&:hover": {
                    transform: "scale(1.02) translateY(-2px)",
                    backgroundColor: `rgba(${parseInt(lvl.start.slice(1, 3), 16)}, ${parseInt(lvl.start.slice(3, 5), 16)}, ${parseInt(lvl.start.slice(5, 7), 16)}, 0.9)`,
                    boxShadow: "0 15px 45px 0 rgba(0, 0, 0, 0.3)",
                    zIndex: 2,
                  },
                  // overlay (::after): tramas o micro-etiquetas
                  '&::after': after && after.background ? {
                    content: '""', position: 'absolute', inset: 0, borderRadius: 0,
                    background: after.background, opacity: after.opacity ?? 0.7,
                    mixBlendMode: after.mixBlendMode ?? 'overlay', pointerEvents: 'none'
                  } : after && after.content ? {
                    content: after.content,
                    position: 'absolute', right: 6, bottom: 4,
                    fontSize: isMini ? '.62rem' : '.68rem', fontWeight: 400,
                    letterSpacing: '.02em', color: after.color ?? '#0B1220',
                    textShadow: '0 1px 0 rgba(255,255,255,.35)'
                  } : undefined,
                }}
              >
                {lvl.label}
              </Button>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
    </Box>
  );
}
